import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Info } from 'lucide-react';
import { DataTypesEnum, ObservableLevelEnumValue } from '../../data/enums';
import { observationService } from '../../services/observationService';
import { Label } from './Label';
import DateHelpers from '../../helpers/dateHelpers';

interface DateFieldProps {
  title?: string;
  onInfoClick?: () => void;
  onChange?: (newDateTime: Date) => void;
  path: string;
  dataLevel: ObservableLevelEnumValue;
  value?: Date;
  entityId: number;
  entityExternalId: string;
  inspectionId: string;
  inspectionCategoryTypeId: string;
  tooltipKey?: string;
  maxDate?: Date;
  minDate?: Date;
  useUTC?: boolean;
  minDateErrorMessage?: string;
  maxDateErrorMessage?: string;
  infoText?: string;
}

export const DateField: React.FC<DateFieldProps> = ({
  title,
  onInfoClick,
  onChange,
  path,
  dataLevel,
  entityId,
  entityExternalId,
  inspectionId,
  inspectionCategoryTypeId,
  tooltipKey,
  maxDate,
  minDate,
  value,
  useUTC,
  minDateErrorMessage,
  maxDateErrorMessage,
  infoText,
}) => {
  const dateInputRef = useRef<HTMLInputElement>(null);

  const [dateTime, setDateTime] = useState<Date>(new Date());
  const [error, setError] = useState<string | null>(null);

  const minDateString = useMemo(() => {
    if (minDate) {
      return minDate.toISOString().split('T')[0];
    }
    return undefined;
  }, [minDate]);

  const maxDateString = useMemo(() => {
    if (maxDate) {
      return maxDate.toISOString().split('T')[0];
    }
    return undefined;
  }, [maxDate]);

  // Format date as YYYY-MM-DD
  const formattedDate = useMemo(() => {
    const dateString = DateHelpers.toLocalDateInputString(dateTime);
    return dateString;
  }, [dateTime]);

  // Load Default
  useEffect(() => {
    const getDefaultValue = async () => {
      if (value) {
        setDateTime(value);
        onChange?.(value);
        return;
      }

      const observation = await observationService.getObservation({
        dataLevel: dataLevel,
        entityId: entityId,
        entityExternalId: entityExternalId,
        inspectionId: inspectionId,
        inspectionCategoryId: inspectionCategoryTypeId,
        path: path,
      });

      if (observation) {
        if (useUTC === true) {
          // Get the DateTime and convert the local time to be the UTC time
          const localDate = new Date(observation.value);
          const utcDate = new Date(
            localDate.getUTCFullYear(),
            localDate.getUTCMonth(),
            localDate.getUTCDate(),
          );
          setDateTime(utcDate);
          return;
        }

        setDateTime(
          observationService.convertValue<Date>(
            observation.value,
            DataTypesEnum.date,
          ),
        );
      } else {
        // Set the current time in the database for this observation
        const currentLocalDate = new Date();
        if (useUTC === true) {
          currentLocalDate.setUTCHours(DateHelpers.midnight, 0, 0, 0);
          currentLocalDate.setFullYear(currentLocalDate.getFullYear());
          currentLocalDate.setMonth(currentLocalDate.getMonth());
          currentLocalDate.setUTCDate(currentLocalDate.getDate());
        }
        const newDateString = DateHelpers.toUTCString(currentLocalDate);
        await observationService.upsertObservation({
          dataLevel: dataLevel,
          entityId: entityId,
          entityExternalId: entityExternalId,
          inspectionCategoryId: inspectionCategoryTypeId,
          inspectionId: inspectionId,
          path: path,
          value: newDateString,
        });
      }
    };

    getDefaultValue();
  }, [
    value,
    dataLevel,
    entityId,
    entityExternalId,
    inspectionId,
    inspectionCategoryTypeId,
    path,
    useUTC,
  ]);

  const handleOpenDatePicker = () => {
    // Trigger the native date picker
    dateInputRef.current?.showPicker?.(); // Modern browsers
    // Fallback if showPicker isn’t supported
    if (!dateInputRef.current?.showPicker) {
      dateInputRef.current?.focus();
      dateInputRef.current?.click();
    }
  };

  const handleDateChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    // if (!onChange) return;

    // Read before the save below: while it is awaited React puts the controlled
    // input back to the date shown, so reading it afterwards kept the old date.
    const pickedValue = e.target.value;

    // Cleared or partial input parses to an Invalid Date whose ISO
    // conversion throws an uncaught RangeError - nothing to persist.
    const newDate = DateHelpers.dateFromDateInput(pickedValue, useUTC === true);
    if (newDate === null) {
      setError(null);
      return;
    }

    // Max Date Validation
    if (maxDate && newDate > maxDate) {
      setError(maxDateErrorMessage || 'Date cannot be in the future');
      return;
    }

    // Min Date Validation, by calendar day: the picker yields local midnight, so a
    // minimum carrying a time of day rejected the very day the picker offers.
    if (
      minDate &&
      DateHelpers.startOfDay(newDate) < DateHelpers.startOfDay(minDate)
    ) {
      setError(minDateErrorMessage || 'Date cannot be in the past');
      return;
    }

    setError(null);

    const newDateString = DateHelpers.toUTCString(newDate);

    const response = await observationService.upsertObservation({
      dataLevel: dataLevel,
      entityId: entityId,
      entityExternalId: entityExternalId,
      inspectionCategoryId: inspectionCategoryTypeId,
      inspectionId: inspectionId,
      path: path,
      value: newDateString,
    });

    const localDate = DateHelpers.dateFromDateInput(pickedValue, false);
    if (localDate) {
      setDateTime(localDate);
    }

    onChange && onChange(newDate);
  };

  return (
    <div className="w-full">
      {(tooltipKey || title) && (
        <div className="mb-4 flex items-center justify-between">
          {title && <Label titleType="fieldTitle">{title}</Label>}
          {tooltipKey && (
            <button
              onClick={onInfoClick}
              className="text-inkMuted transition-colors hover:text-ink"
              aria-label="More information"
            >
              <Info className="size-5" />
            </button>
          )}
        </div>
      )}

      <div className="cursor-pointer space-y-4">
        <div
          className="flex cursor-pointer items-center rounded-sm bg-gray-100 p-3"
          onClick={handleOpenDatePicker}
        >
          <div className="flex w-full cursor-pointer items-center gap-2">
            <svg
              className="size-5 shrink-0 cursor-pointer text-inkMuted"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            <input
              type="date"
              ref={dateInputRef}
              value={formattedDate}
              min={minDateString}
              max={maxDateString}
              onChange={handleDateChange}
              className="w-full cursor-pointer bg-transparent text-ink focus:outline-none"
            />
          </div>
        </div>
        {infoText && <div className="text-sm text-inkMuted">{infoText}</div>}
        {error && <div className="text-sm text-red-500">{error}</div>}
      </div>
    </div>
  );
};
